// File: nodes/CheckChannelConsentNode.jsx
import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { FiCopy, FiEdit, FiTrash2, FiShield } from 'react-icons/fi';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { getConsentChannel } from '../utils/consentChannels';

const CheckChannelConsentNode = ({ data }) => {
  // Channels come from /channel/list now, so the label saved on the node is the
  // source of truth; the static map is only a fallback for nodes saved before that.
  const channelMeta = getConsentChannel(data.channel);
  const channelName = data.channelLabel || channelMeta?.label || '';
  const channel = channelName ? { ...(channelMeta || {}), label: channelName } : null;
  const ChannelIcon = channel?.icon;

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="#7C3AED"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="260px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ width: 12, height: 12, background: '#7C3AED' }}
      />

      {/* Header */}
      <Flex
        bg="#7C3AED"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FiShield size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          Check Channel Consent
        </Text>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit?.(data.id);
            }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDuplicate?.(data.id);
            }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(data.id);
            }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3}>
        <Text fontSize="sm" fontWeight="medium" mb={3}>
          {data.label || 'Check Channel Consent'}
        </Text>

        {/* Channel */}
        <Flex align="center" gap={2} mb={3}>
          {channel ? (
            <Flex
              flex={1}
              align="center"
              gap={1.5}
              bg="purple.50"
              border="1px solid"
              borderColor="purple.200"
              borderRadius="lg"
              px={2.5}
              py={1.5}
            >
              {/* dynamic channels have no static icon — render the name alone */}
              {ChannelIcon && (
                <Box color="purple.600" flexShrink={0}>
                  <ChannelIcon size={13} />
                </Box>
              )}
              <Text fontSize="xs" fontWeight="semibold" color="purple.900">
                {channel.label}
              </Text>
            </Flex>
          ) : (
            <Flex
              flex={1}
              justify="center"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              px={2.5}
              py={1.5}
            >
              <Text fontSize="xs" color="gray.500">
                No channel
              </Text>
            </Flex>
          )}

        </Flex>

        {/* Branches */}
        <VStack spacing={2} align="stretch">
          <Box
            position="relative"
            border="1px solid"
            borderColor="green.200"
            borderRadius="lg"
            p={2}
            bg="green.50"
          >
            <Flex align="center" gap={2}>
              <Box color="green.600" flexShrink={0}>
                <FaCheckCircle size={13} />
              </Box>
              <Text fontSize="xs" fontWeight="medium" color="green.700">
                {data.enabledLabel || 'Enabled'}
              </Text>
            </Flex>
            <Handle
              type="source"
              position={Position.Right}
              id="enabled"
              // See flowHandles.css .branch-handle: this row's own border (1px) + the
              // node body's padding (p={3} = 12px) + the node's border (2px) + half the
              // rendered 10px dot (6px forced width + this row's 2px white ring) = the
              // offset that actually reaches the node's outer edge, not just the row's.
              className="branch-handle"
              style={{
                width: 10,
                height: 10,
                background: '#22c55e',
                border: '2px solid white',
                top: '50%',
                transform: 'translateY(-50%)',
                '--flow-branch-offset': '-20px',
              }}
            />
          </Box>

          <Box
            position="relative"
            border="1px solid"
            borderColor="red.200"
            borderRadius="lg"
            p={2}
            bg="red.50"
          >
            <Flex align="center" gap={2}>
              <Box color="red.500" flexShrink={0}>
                <FaTimesCircle size={13} />
              </Box>
              <Text fontSize="xs" fontWeight="medium" color="red.700">
                {data.disabledLabel || 'Disabled'}
              </Text>
            </Flex>
            <Handle
              type="source"
              position={Position.Right}
              id="disabled"
              className="branch-handle"
              style={{
                width: 10,
                height: 10,
                background: '#ef4444',
                border: '2px solid white',
                top: '50%',
                transform: 'translateY(-50%)',
                '--flow-branch-offset': '-20px',
              }}
            />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default CheckChannelConsentNode;
